/**
 * COMPONENTE DE GESTIÓN DE MINORISTAS CON FLUJO PASO A PASO
 * 
 * Este componente implementa un proceso guiado para la gestión de clientes y candidatos,
 * permitiendo su registro, selección, asignación de tests y visualización de resultados.
 * 
 * FUNCIONALIDADES PRINCIPALES v1.5:
 * - Selección de cliente con detalles visibles en tiempo real
 * - Registro de nuevos candidatos mediante modal con validación avanzada
 * - Gestión de candidatos asociados al cliente seleccionado
 * - Asignación de cantidad de tests por candidato
 * - Proceso dividido en 4 pasos con navegación controlada:
 *   1. Selección de Cliente
 *   2. Datos de Candidatos
 *   3. Asignación de Tests
 *   4. Resultados finales
 * - Validaciones dinámicas por campo:
 *   * Documento: solo números, mínimo 6 dígitos
 *   * Nombre: solo letras y espacios, mínimo 2 caracteres
 *   * Celular: solo números, mínimo 7 dígitos
 *   * Correo: formato válido de email
 *   * País y Ciudad: selectores dependientes
 * - Soporte para selección múltiple de candidatos
 * - Prevención de duplicados por documento en candidatos
 * - Persistencia temporal en memoria de clientes y candidatos de ejemplo
 * - Integración con buscador global de clientes
 * - Interfaz responsiva con Material UI y componentes personalizados
 * 
 * MEJORAS DE UX v1.5:
 * - Labels y tipografía optimizados para mayor legibilidad
 * - Espaciado uniforme en todos los campos del modal
 * - Validación en tiempo real con feedback visual inmediato
 * - Estados de botones y campos dependientes de validaciones
 * - Mensajes claros y específicos de error por campo
 * - Flujo paso a paso con indicador visual (StepIndicator)
 * - Redirección entre pasos condicionada al cumplimiento de requisitos
 * - Selectores dependientes para País y Ciudad
 * - Indicadores visuales de selección múltiple de candidatos
 * - Cálculo dinámico del total de tests en la asignación
 * - Pantalla final de confirmación con resumen de cliente, candidatos y tests
 * 
 * @component
 * @returns {JSX.Element} Interfaz de gestión paso a paso de clientes y candidatos
 * 
 * @author Diego Alejandro y Feliciano
 * @version 1.5
 * @since 2025-09
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from 'prop-types';
import * as XLSX from "xlsx";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import { Divider, MenuItem } from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Custom components
import StepIndicator from "components/StepIndicator";
import MDModal from "components/MDModal";

// Services
import emailService from "services/emailService";

// Context
import { useMaterialUIController } from "context";

// Mapa de países con ciudades
const countryCityMap = {
  Argentina: ["Buenos Aires", "Córdoba", "Rosario"],
  Bolivia: ["La Paz", "Santa Cruz", "Cochabamba"],
  Brasil: ["São Paulo", "Rio de Janeiro", "Brasília"],
  Chile: ["Santiago", "Valparaíso", "Concepción"],
  Colombia: ["Bogotá", "Medellín", "Cali"],
  CostaRica: ["San José", "Alajuela", "Cartago"],
  Cuba: ["La Habana", "Santiago de Cuba", "Camagüey"],
  Ecuador: ["Quito", "Guayaquil", "Cuenca"],
  ElSalvador: ["San Salvador", "Santa Ana", "San Miguel"],
  Guatemala: ["Ciudad de Guatemala", "Quetzaltenango", "Escuintla"],
  Honduras: ["Tegucigalpa", "San Pedro Sula", "La Ceiba"],
  México: ["Ciudad de México", "Guadalajara", "Monterrey"],
  Nicaragua: ["Managua", "León", "Granada"],
  Panamá: ["Ciudad de Panamá", "Colón", "David"],
  Paraguay: ["Asunción", "Ciudad del Este", "Encarnación"],
  Perú: ["Lima", "Arequipa", "Trujillo"],
  PuertoRico: ["San Juan", "Ponce", "Mayagüez"],
  RepúblicaDominicana: ["Santo Domingo", "Santiago", "La Romana"],
  Uruguay: ["Montevideo", "Salto", "Punta del Este"],
  Venezuela: ["Caracas", "Maracaibo", "Valencia"],
};

/**
 * Componente principal para la gestión de minoristas con proceso paso a paso
 * 
 * Funcionalidades principales:
 * - Selección de cliente con detalles
 * - Registro de nuevos candidatos
 * - Selección de candidatos para tests
 * - Asignación de cantidad de tests
 * - Visualización de resultados finales
 * 
 * El proceso consta de 4 pasos:
 * 1. Selección de Cliente
 * 2. Datos de Candidatos 
 * 3. Asignación de Tests
 * 4. Resultados
 */
const GestionMinoristasPasos = () => {
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { searchTerm } = controller; // Usar el buscador global

  const [selectedClient, setSelectedClient] = useState(null);
  const [currentStep, setCurrentStep] = useState(0); // 0-indexed for StepIndicator
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [testQuantity, setTestQuantity] = useState("");
  
  const TEST_NAME = "Quadrante H"; // Nombre fijo del test

  // Estados para carga masiva
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    color: "success",
  });

  // Estados para el modal de registro de candidato
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    documento: "",
    nombre: "",
    celular: "",
    correo: "",
    pais: "",
    ciudad: ""
  });
  
  // Estados para errores de validación
  const [formErrors, setFormErrors] = useState({
    documento: "",
    nombre: "",
    celular: "",
    correo: "",
    pais: "",
    ciudad: ""
  });

  /**
   * Configuración de pasos para el StepIndicator
   * Cada paso incluye key única, label descriptivo e icono de Material-UI
   */
  const STEPS = [
    { key: "seleccion_cliente", label: "Selección de Cliente", icon: "person_add" },
    { key: "datos_candidatos", label: "Carga de Candidatos", icon: "group" }, // Cambiado a group
    { key: "resumen_envio", label: "Resumen y Envío", icon: "send" }, // Cambiado a send
  ];

  /**
   * Estado de clientes disponibles
   * En producción, estos datos vendrían de una API
   */
  const [clients, setClients] = useState([
    { id: "001", documento: "1002220230", nombre: "Ana Sofia", correo: "correo@gamail.com" },
    { id: "002", documento: "1002220555", nombre: "lorenzo p", correo: "lp@gamail.com" }
  ]);

  // Los candidatos se cargarán únicamente desde la carga masiva (Excel)
  // No hay datos de ejemplo iniciales

  // ========== FUNCIONES DE VALIDACIÓN ==========

  /**
   * Valida si una cadena contiene solo números
   * @param {string} value - Valor a validar
   * @returns {boolean} true si contiene solo números
   */
  const isOnlyNumbers = (value) => {
    return /^\d+$/.test(value) || value === "";
  };

  /**
   * Valida si una cadena contiene solo letras y espacios
   * @param {string} value - Valor a validar
   * @returns {boolean} true si contiene solo letras y espacios
   */
  const isOnlyLetters = (value) => {
    return /^[a-zA-ZáéíóúüñÁÉÍÓÚÜÑ\s]+$/.test(value) || value === "";
  };

  /**
   * Valida formato de email
   * @param {string} email - Email a validar
   * @returns {boolean} true si el formato es válido
   */
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) || email === "";
  };

  /**
   * Valida un campo específico y retorna mensaje de error si hay uno
   * @param {string} field - Campo a validar
   * @param {string} value - Valor del campo
   * @returns {string} Mensaje de error o cadena vacía si es válido
   */
  const validateField = (field, value) => {
    switch (field) {
      case 'documento':
        if (!value) return "El documento es obligatorio";
        if (!isOnlyNumbers(value)) return "El documento debe contener solo números";
        if (value.length < 6) return "El documento debe tener al menos 6 dígitos";
        return "";

      case 'nombre':
        if (!value) return "El nombre es obligatorio";
        if (!isOnlyLetters(value)) return "El nombre debe contener solo letras";
        if (value.trim().length < 2) return "El nombre debe tener al menos 2 caracteres";
        return "";

      case 'celular':
        if (!value) return "El celular es obligatorio";
        if (!isOnlyNumbers(value)) return "El celular debe contener solo números";
        if (value.length < 7) return "El celular debe tener al menos 7 dígitos";
        return "";

      case 'correo':
        if (!value) return "El correo es obligatorio";
        if (!isValidEmail(value)) return "Formato de correo inválido";
        return "";

      case 'pais':
        if (!value) return "El país es obligatorio";
        return "";

      case 'ciudad':
        if (!value) return "La ciudad es obligatoria";
        return "";

      default:
        return "";
    }
  };

  // ========== FUNCIONES DEL MODAL ==========

  /**
   * Abre el modal de registro de candidato
   */
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  /**
   * Cierra el modal y resetea el formulario
   */
  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Resetear formulario al cerrar
    setFormData({
      documento: "",
      nombre: "",
      celular: "",
      correo: "",
      pais: "",
      ciudad: ""
    });
    // Resetear errores
    setFormErrors({
      documento: "",
      nombre: "",
      celular: "",
      correo: "",
      pais: "",
      ciudad: ""
    });
  };

  /**
   * Maneja cambios en los campos del formulario del modal
   * Aplica validaciones en tiempo real
   * @param {string} field - Campo que cambió
   * @param {string} value - Nuevo valor del campo
   */
  const handleFormChange = (field, value) => {
    let filteredValue = value;
    switch (field) {
      case "documento":
      case "celular":
        filteredValue = value.replace(/\D/g, "");
        break;
      case "correo":
        filteredValue = value.toLowerCase().trim();
        break;
      default:
        filteredValue = value;
    }
    setFormData((prev) => ({ ...prev, [field]: filteredValue }));
    const errorMessage = validateField(field, filteredValue);
    setFormErrors((prev) => ({ ...prev, [field]: errorMessage }));
  };

  /**
   * Guarda un nuevo candidato
   * Valida que todos los campos requeridos estén completos y sean válidos
   */
  const handleSaveCandidate = () => {
    const { documento, nombre, celular, correo, pais, ciudad } = formData;
    
    // Validar todos los campos
    const newErrors = {};
    let hasErrors = false;

    Object.keys(formData).forEach(field => {
      const error = validateField(field, formData[field]);
      newErrors[field] = error;
      if (error) hasErrors = true;
    });

    // Actualizar errores
    setFormErrors(newErrors);

    // Si hay errores, no guardar
    if (hasErrors) {
      return;
    }

    // Verificar que no existe un candidato con el mismo documento
    const existingCandidate = candidates.find(c => c.documento === documento);
    if (existingCandidate) {
      setFormErrors(prev => ({
        ...prev,
        documento: "Ya existe un candidato con este documento"
      }));
      return;
    }
    
    if (documento && nombre && celular && correo && pais && ciudad) {
      // Generar nuevo ID para el candidato
      const newId = Math.max(...candidates.map(c => c.id), 0) + 1;
      
      const newCandidate = {
        id: newId,
        documento: documento.trim(),
        nombre: nombre.trim(),
        celular: celular.trim(),
        correo: correo.trim(),
        pais: pais.trim(),
        ciudad: ciudad.trim()
      };
      
      // Agregar nuevo candidato a la lista
      setCandidates(prev => [...prev, newCandidate]);
      
      // Cerrar modal y resetear formulario
      handleCloseModal();
      
      console.log("Candidato guardado:", newCandidate);
    }
  };

  // ========== FUNCIONES DE FILTRADO Y BÚSQUEDA ==========

  /**
   * Filtra clientes basado en el término de búsqueda global
   * Busca en documento, nombre y correo
   */
  const filteredClients = useMemo(() => {
    if (!searchTerm) return clients;
    const lowercaseSearch = searchTerm.toLowerCase();
    return clients.filter(client =>
      client.documento.includes(lowercaseSearch) ||
      client.nombre.toLowerCase().includes(lowercaseSearch) ||
      client.correo.toLowerCase().includes(lowercaseSearch)
    );
  }, [clients, searchTerm]);

  // ========== FUNCIONES DE SELECCIÓN ==========

  /**
   * Maneja la selección/deselección de un cliente
   * @param {Object} client - Cliente a seleccionar
   */
  const handleClientSelect = useCallback((client) => {
    if (selectedClient && selectedClient.id === client.id) {
      // Deseleccionar si es el mismo cliente
      setSelectedClient(null);
      setCandidates([]);
      setSelectedCandidates([]);
      setTestQuantity("");
    } else {
      // Seleccionar un nuevo cliente
      setSelectedClient(client);
      setCandidates([]); // Los candidatos se cargarán desde carga masiva
      setTestQuantity(""); // Resetear cantidad de tests al cambiar de cliente
    }
  }, [selectedClient]);

  /**
   * Maneja la selección/deselección individual de candidatos
   * @param {number} candidateId - ID del candidato
   * @param {boolean} isSelected - Si está seleccionado o no
   */
  const handleCandidateSelect = useCallback((candidateId, isSelected) => {
    if (isSelected && selectedCandidates.length >= parseInt(testQuantity)) {
      showNotification(`Solo puedes seleccionar hasta ${testQuantity} candidatos.`, "warning");
      return; // No permitir seleccionar más allá del límite
    }
    setSelectedCandidates(prev =>
      isSelected
        ? [...prev, candidateId]
        : prev.filter(id => id !== candidateId)
    );
  }, [selectedCandidates, testQuantity]);

  /**
   * Maneja la selección/deselección de todos los candidatos
   * @param {Event} e - Evento del checkbox
   */
  const handleSelectAllCandidates = useCallback((e) => {
    if (e.target.checked) {
      setSelectedCandidates(candidates.map(c => c.id));
    } else {
      setSelectedCandidates([]);
    }
  }, [candidates]);

  // Funciones para carga masiva
  const showNotification = (message, color = "success") => {
    setNotification({
      open: true,
      message,
      color,
    });
  };

  const closeNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  const handleDownloadTemplate = () => {
    const headers = [
      "Nombre Completo",
      "Num Identificación", 
      "Correo",
      "Fecha Nacimiento",
      "Edad",
      "Genero",
      "Direccion",
      "Nivel Educativo",
      "Experiencia laboral",
      "Habilidades Destacadas",
      "Motivacion por el Cargo",
      "Cargo Aplicado",
      "Ubicacion Geografica",
      "Pais",
    ];

    const ws = XLSX.utils.aoa_to_sheet([headers]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Plantilla");
    XLSX.writeFile(wb, "Plantilla_Candidatos_Minoristas.xlsx");

    showNotification("Plantilla descargada correctamente", "success");
  };

  // Función para convertir números de serie de Excel a fechas
  const excelDateToJSDate = (excelDate) => {
    if (typeof excelDate === 'string' && excelDate.includes('-')) {
      return excelDate;
    }

    if (typeof excelDate === 'number') {
      const jsDate = new Date((excelDate - 25569) * 86400 * 1000);
      return jsDate.toISOString().split('T')[0];
    }

    if (excelDate instanceof Date) {
      return excelDate.toISOString().split('T')[0];
    }

    return excelDate;
  };

  // Función para procesar los datos de Excel antes de enviarlos
  const processExcelData = (jsonData) => {
    return jsonData.map((row, index) => {
      const processedRow = { ...row };

      // Convertir la fecha de nacimiento si existe
      if (processedRow['Fecha Nacimiento']) {
        processedRow['Fecha Nacimiento'] = excelDateToJSDate(processedRow['Fecha Nacimiento']);
      }

      // Agregar ID único basado en el índice
      processedRow.id = candidates.length + index + 1;

      return processedRow;
    });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        setLoading(true);
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          raw: false,
          dateNF: 'yyyy-mm-dd'
        });

        if (jsonData.length === 0) {
          showNotification("El archivo está vacío o no tiene datos válidos", "warning");
          return;
        }

        // Procesar las fechas antes de agregar a candidatos
        const processedData = processExcelData(jsonData);

        // Mapear los datos del Excel a la estructura de candidatos
        const newCandidates = processedData.map(row => ({
          id: row.id,
          documento: row['Num Identificación'] || '',
          nombre: row['Nombre Completo'] || '',
          correo: row['Correo'] || '',
          fechaNacimiento: row['Fecha Nacimiento'] || '',
          edad: row['Edad'] || '',
          genero: row['Genero'] || '',
          direccion: row['Direccion'] || '',
          nivelEducativo: row['Nivel Educativo'] || '',
          experienciaLaboral: row['Experiencia laboral'] || '',
          habilidadesDestacadas: row['Habilidades Destacadas'] ? row['Habilidades Destacadas'].split(',').map(h => h.trim()) : [],
          motivacionCargo: row['Motivacion por el Cargo'] || '',
          cargoAplicado: row['Cargo Aplicado'] || '',
          ubicacionGeografica: row['Ubicacion Geografica'] || '',
          pais: row['Pais'] || 'Colombia',
        }));

        // Agregar los nuevos candidatos a la lista existente
        setCandidates(prev => [...prev, ...newCandidates]);

        // Limpiar el input de archivo
        event.target.value = '';

        showNotification(`Archivo cargado correctamente: ${newCandidates.length} candidatos agregados`, "success");

      } catch (error) {
        console.error("Error al procesar el archivo:", error);
        showNotification(error.message || "Error al procesar el archivo", "error");
      } finally {
        setLoading(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Función para enviar correos a candidatos seleccionados usando la plantilla del backend
  const handleSendTestEmails = async () => {
    try {
      setLoading(true);
      showNotification("Enviando correos...", "info");

      // Obtener candidatos seleccionados con sus datos completos
      const selectedCandidatesData = candidates.filter(candidate => 
        selectedCandidates.includes(candidate.id)
      );
      // URL del test usando la variable de entorno de Supabase con la ruta correcta de login
      const testLoginUrl = `${process.env.REACT_APP_SUPABASE_URL}/authentication/sign-in`;

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // Enviar correo a cada candidato seleccionado usando sendTestCredentials
      for (const candidate of selectedCandidatesData) {
        try {
          await emailService.sendTestCredentials({
            id_candidato: candidate.documento, // Usar documento como ID temporal
            id_empresa: selectedClient.id, // Usar ID del cliente seleccionado
            test_url: testLoginUrl + "/v1/sign-in",
            test_name: TEST_NAME,
            candidate_email: candidate.correo,
            candidate_full_name: candidate.nombre
          });

          successCount++;
        } catch (error) {
          errorCount++;
          errors.push({
            candidate: candidate.nombre,
            email: candidate.correo,
            error: error.message
          });
        }
      }

      // Mostrar resultados
      if (successCount > 0) {
        let message = `Correos enviados exitosamente a ${successCount} candidato(s)`;
        if (errorCount > 0) {
          message += `. ${errorCount} correo(s) fallaron.`;
        }
        showNotification(message, errorCount > 0 ? "warning" : "success");
        
        // Mostrar errores detallados si los hay
        if (errors.length > 0) {
          setTimeout(() => {
            const errorDetails = errors.map(err => 
              `${err.candidate} (${err.email}): ${err.error}`
            ).join('\n');
            showNotification(`Errores detallados:\n${errorDetails}`, "error");
          }, 3000);
        }
      } else {
        showNotification("No se pudo enviar ningún correo. Verifica la configuración.", "error");
      }

    } catch (error) {
      console.error('Error al enviar correos:', error);
      showNotification(error.message || "Error al enviar correos", "error");
    } finally {
      setLoading(false);
    }
  };

  // Avanzar al siguiente paso
  const handleNext = () => {
    if (currentStep === 0 && selectedClient && testQuantity) { // Paso 1: Selección de Cliente y Cantidad de Test
      setCurrentStep(1); // Mover a Carga de Candidatos
    } else if (currentStep === 1 && selectedCandidates.length === parseInt(testQuantity)) { // Paso 2: Selección de Candidatos
      setCurrentStep(2); // Mover a Resumen y Envío
    } else if (currentStep === 2) { // Paso 3: Resumen y Envío - Acciones finales
      // Enviar correos a candidatos seleccionados
      handleSendTestEmails();
    }
  };

  /**
   * Vuelve al paso anterior
   */
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // ========== COMPONENTES DE CELDAS PARA LAS TABLAS ==========

  /**
   * Componente para la celda de acción en la tabla de clientes
   * Muestra botón de selección/deselección
   */
  const ActionCell = ({ row }) => {
    const { id } = row.original;
    const isSelected = selectedClient && selectedClient.id === id;
    return (
      <MDButton
        variant="gradient"
        color={isSelected ? "info" : "secondary"}
        onClick={() => handleClientSelect(row.original)}
        size="small"
      >
        {isSelected ? "seleccionado" : "selección"}
      </MDButton>
    );
  };

  ActionCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }).isRequired
    }).isRequired
  };

  /**
   * Componente para la celda de checkbox en la tabla de candidatos
   * Permite selección múltiple de candidatos
   */
  const CheckboxCell = ({ row }) => {
    const { id } = row.original;
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center">
        <input
          type="checkbox"
          checked={selectedCandidates.includes(id)}
          onChange={(e) => handleCandidateSelect(id, e.target.checked)}
          style={{ marginRight: "8px" }}
        />
        {id}
      </MDBox>
    );
  };

  CheckboxCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      }).isRequired
    }).isRequired
  };

  // ========== RENDERIZADO DE CONTENIDO POR PASO ==========

  /**
   * Renderiza el contenido específico según el paso actual
   * @returns {JSX.Element} Contenido del paso actual
   */
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Paso 1: Selección de Cliente
        return (
          <Grid container spacing={3} sx={{ flexGrow: 1 }}>
            {/* Columna izquierda: Tabla de clientes */}
            <Grid item xs={12} lg={7}>
              <Card sx={{ height: "100%" }}>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="primary"
                  borderRadius="lg"
                  coloredShadow="primary"
                  mb={3}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6" color="white">
                    Tabla de Clientes
                  </MDTypography>
                  
                  <MDButton
                    variant="contained"
                    color="white"
                    size="small"
                    onClick={handleOpenModal}
                    startIcon={<Icon>add</Icon>}
                    sx={{
                      color: "primary.main",
                      "&:hover": {
                        backgroundColor: "white",
                        opacity: 0.9
                      }
                    }}
                  >
                    Nuevo Candidato
                  </MDButton>
                </MDBox>

                <MDBox pt={0} px={3}>
                  <DataTable
                    table={{
                      columns: [
                        { Header: "Id", accessor: "id", width: "5%" },
                        { Header: "Documento", accessor: "documento", width: "15%" },
                        { Header: "Nombre", accessor: "nombre", width: "25%" },
                        { Header: "Correo", accessor: "correo", width: "30%" },
                        {
                          Header: "Acción",
                          accessor: "accion",
                          width: "15%",
                          Cell: ActionCell,
                        },
                      ],
                      rows: filteredClients.map(client => ({ ...client, original: client })),
                    }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>
              </Card>
            </Grid>

            {/* Columna derecha: Detalles del Cliente y Cantidad de Tests */}
            <Grid item xs={12} lg={5}>
              <Card sx={{ height: "100%" }}>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  mb={3}
                >
                  <MDTypography variant="h6" color="white">
                    Detalles del Cliente Seleccionado
                  </MDTypography>
                </MDBox>

                <MDBox p={3} sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  {selectedClient ? (
                    <MDBox>
                      <MDTypography variant="body2" fontWeight="regular" color="text" mb={1}>
                        <MDTypography component="span" fontWeight="bold">Documento:</MDTypography> {selectedClient.documento}
                      </MDTypography>
                      <MDTypography variant="body2" fontWeight="regular" color="text" mb={1}>
                        <MDTypography component="span" fontWeight="bold">Nombre:</MDTypography> {selectedClient.nombre}
                      </MDTypography>
                      <MDTypography variant="body2" fontWeight="regular" color="text" mb={3}>
                        <MDTypography component="span" fontWeight="bold">Correo:</MDTypography> {selectedClient.correo}
                      </MDTypography>

                      <MDInput
                        select
                        label={`Cantidad de candidatos a evaluar con ${TEST_NAME}`}
                        fullWidth
                        value={testQuantity}
                        onChange={(e) => setTestQuantity(e.target.value)}
                        sx={{
                          mb: 2,
                          '& .MuiSelect-select': {
                            textAlign: testQuantity ? 'left' : 'center',
                            padding: '16px 14px',
                            minHeight: '48px'
                          },
                          '& .MuiInputBase-root': {
                            minHeight: '56px'
                          }
                        }}
                      >
                        <MenuItem value="">Seleccionar cantidad</MenuItem>
                        {[1, 2, 3, 4, 5].map((num) => (
                          <MenuItem key={num} value={num}>
                            {num}
                          </MenuItem>
                        ))}
                      </MDInput>

                      {/* Lista visual de números */}
                      <MDBox bgColor="light" p={1.5} borderRadius="lg" border={1} borderColor="grey.300">
                        <MDTypography variant="body2" color="text" lineHeight={1.5}>
                          Selecciona la cantidad de candidatos para aplicar el test &quot;{TEST_NAME}&quot;.
                        </MDTypography>
                        <MDTypography variant="caption" color="text" mt={0.5}>
                          Por ejemplo, si seleccionas &apos;3&apos;, deberás elegir 3 candidatos en el siguiente paso.
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  ) : (
                    <MDBox textAlign="center" py={4} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Icon fontSize="large" color="secondary">person_outline</Icon>
                      <MDTypography variant="body2" color="text" mt={2}>
                        Selecciona un cliente para ver sus datos y asignar tests.
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        );

      case 1: // Paso 2: Datos de Candidatos
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MDBox bgColor="light" p={3} borderRadius="lg" mb={3}>
                <MDTypography variant="h6" color="dark" mb={2}>Cliente Seleccionado:</MDTypography>
                <MDTypography variant="body2" color="text"><strong>Documento:</strong> {selectedClient?.documento}</MDTypography>
                <MDTypography variant="body2" color="text"><strong>Nombre:</strong> {selectedClient?.nombre}</MDTypography>
                <MDTypography variant="body2" color="text"><strong>Correo:</strong> {selectedClient?.correo}</MDTypography>
              </MDBox>
            </Grid>

            {/* Sección de Carga Masiva */}
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  mb={3}
                >
                  <MDTypography variant="h6" color="white">
                    Carga Masiva de Candidatos
                  </MDTypography>
                </MDBox>
                <MDBox p={3}>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12}>
                      <MDTypography variant="h6">CARGAR MASIVO</MDTypography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <MDInput
                        type="file"
                        fullWidth
                        accept=".xlsx,.xls"
                        onChange={handleFileUpload}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MDButton
                        variant="gradient"
                        color="info"
                        fullWidth
                        onClick={handleDownloadTemplate}
                        disabled={loading}
                      >
                        <Icon>download</Icon>&nbsp; Descargar Plantilla
                      </MDButton>
                    </Grid>
                  </Grid>
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="primary"
                  borderRadius="lg"
                  coloredShadow="primary"
                  mb={3}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6" color="white">
                    Tabla de Candidatos ({candidates.length} total)
                  </MDTypography>
                  
                  <MDButton
                    variant="contained"
                    color="white"
                    size="small"
                    onClick={handleOpenModal}
                    startIcon={<Icon>add</Icon>}
                    sx={{
                      color: "primary.main",
                      "&:hover": {
                        backgroundColor: "white",
                        opacity: 0.9
                      }
                    }}
                  >
                    Nuevo Candidato
                  </MDButton>
                </MDBox>
                <MDBox pt={0} px={3}>
                  {candidates.length > 0 ? (
                    <DataTable
                      table={{
                        columns: [
                          {
                            Header: (
                              <MDBox display="flex" justifyContent="center" alignItems="center">
                                <input
                                  type="checkbox"
                                  checked={selectedCandidates.length === candidates.length && candidates.length > 0}
                                  onChange={handleSelectAllCandidates}
                                  style={{ marginRight: "8px" }} // Estilo inline para un pequeño margen
                                />
                                Id
                              </MDBox>
                            ),
                            accessor: "id",
                            width: "5%",
                            Cell: CheckboxCell,
                          },
                          { Header: "Documento", accessor: "documento", width: "25%" },
                          { Header: "Nombre", accessor: "nombre", width: "30%" },
                          { Header: "Correo", accessor: "correo", width: "40%" },
                        ],
                        rows: candidates.map(candidate => ({ ...candidate, original: candidate })),
                      }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  ) : (
                    <MDBox textAlign="center" py={4}>
                      <Icon fontSize="large" color="secondary">upload_file</Icon>
                      <MDTypography variant="body2" color="text" mt={2}>
                        No hay candidatos cargados. Utiliza la carga masiva para agregar candidatos desde un archivo Excel.
                      </MDTypography>
                    </MDBox>
                  )}

                  {testQuantity > 0 && selectedCandidates.length < parseInt(testQuantity) && ( // Solo mostrar si falta por seleccionar y testQuantity es válido
                    <MDBox mt={2} p={1.5} borderRadius="md" bgColor="info" color="white">
                      <MDTypography variant="caption" color="white" fontWeight="medium">
                        👉 Selecciona {parseInt(testQuantity) - selectedCandidates.length} candidato(s) más para continuar.
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              </Card>
            </Grid>

            {selectedCandidates.length > 0 && (
              <Grid item xs={12}>
                <MDBox bgColor="info" p={3} borderRadius="lg">
                  <MDTypography variant="body2" color="white">
                    {selectedCandidates.length} candidato(s) seleccionado(s)
                  </MDTypography>
                </MDBox>
              </Grid>
            )}
          </Grid>
        );

      case 2: // Resumen y Envío (Step 3)
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="primary"
                  borderRadius="lg"
                  coloredShadow="primary"
                  mb={3}
                >
                  <MDTypography variant="h6" color="white">
                    Resumen y Envío
                  </MDTypography>
                </MDBox>

                <MDBox p={3}>
                  <MDBox bgColor="light" p={3} borderRadius="lg" border={1} borderColor="grey.300">
                    <MDTypography variant="h6" color="dark" mb={2}>Cliente Seleccionado:</MDTypography>
                    <MDTypography variant="body2" color="text"><strong>Documento:</strong> {selectedClient?.documento}</MDTypography>
                    <MDTypography variant="body2" color="text"><strong>Nombre:</strong> {selectedClient?.nombre}</MDTypography>
                    <MDTypography variant="body2" color="text" mb={2}><strong>Correo:</strong> {selectedClient?.correo}</MDTypography>

                    <Divider sx={{ my: 2 }} />

                    <MDTypography variant="h6" color="dark" mb={2}>Detalles del Test:</MDTypography>
                    <MDTypography variant="body2" color="text" lineHeight={1.5}>
                      Nombre del Test: <strong>{TEST_NAME}</strong>
                    </MDTypography>
                    <MDTypography variant="body2" color="text" lineHeight={1.5}>
                      Candidatos a evaluar: <strong>{parseInt(testQuantity) || 0}</strong>
                    </MDTypography>
                    <MDTypography variant="body2" color="text" lineHeight={1.5}>
                      Candidatos seleccionados: <strong>{selectedCandidates.length}</strong>
                    </MDTypography>
                    <MDTypography variant="body1" fontWeight="medium" color="dark" mt={1}>
                      Total de tests a enviar: <strong>{selectedCandidates.length}</strong>
                    </MDTypography>

                    {selectedCandidates.length < parseInt(testQuantity) && ( // Mostrar advertencia si no se han seleccionado suficientes candidatos
                      <MDBox mt={2} p={1.5} borderRadius="md" bgColor="warning" color="white">
                        <MDTypography variant="caption" color="white" fontWeight="medium">
                          ⚠️ Por favor, selecciona {parseInt(testQuantity) - selectedCandidates.length} candidato(s) más para cumplir con la cantidad requerida.
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  // ========== RENDERIZADO PRINCIPAL ==========

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={8} pb={3}>
        <Grid container justifyContent="center" spacing={3}>
          <Grid item xs={12} lg={10} xl={9}>
            {/* Indicador de pasos */}
            <MDBox mb={4}>
              <StepIndicator steps={STEPS} activeStep={currentStep} />
            </MDBox>

            {/* Contenido principal */}
            <Card>
              <MDBox pt={8} px={3} pb={3}>
                {renderStepContent()}
              </MDBox>

              {/* Botones de navegación */}
              <MDBox display="flex" justifyContent="space-between" p={3} pt={0}>
                <MDButton
                  variant="gradient"
                  color="secondary"
                  onClick={handleBack}
                  disabled={currentStep === 0}
                >
                  Volver
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="success"
                  onClick={handleNext}
                  disabled={
                    (currentStep === 0 && (!selectedClient || !testQuantity)) ||
                    (currentStep === 1 && selectedCandidates.length !== parseInt(testQuantity))
                  }
                >
                  {currentStep === 2 ? 'Confirmar y Enviar' : 'Siguiente'}
                </MDButton>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Modal para registro de nuevo candidato con labels mejorados */}
      <MDModal
        open={isModalOpen}
        onClose={handleCloseModal}
        title="Registro de Nuevo Candidato"
        size="lg"
      >
        <MDBox p={3}>
          <Grid container spacing={3}>
            {/* Documento y Nombre */}
            <Grid item xs={12} md={6}>
              <MDInput
                label="Documento"
                fullWidth
                variant="outlined"
                value={formData.documento}
                onChange={(e) =>
                  handleFormChange("documento", e.target.value)
                }
                error={!!formErrors.documento}
                helperText={formErrors.documento}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#344767',
                    transform: 'translate(14px, 16px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.85)',
                      fontSize: '0.875rem',
                      fontWeight: 600
                    }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                    minHeight: '48px',
                    '& fieldset': {
                      borderColor: '#e0e6ed'
                    },
                    '&:hover fieldset': {
                      borderColor: '#5e72e4'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5e72e4'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                label="Nombre Completo"
                fullWidth
                variant="outlined"
                value={formData.nombre}
                onChange={(e) => handleFormChange("nombre", e.target.value)}
                error={!!formErrors.nombre}
                helperText={formErrors.nombre}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#344767',
                    transform: 'translate(14px, 16px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.85)',
                      fontSize: '0.875rem',
                      fontWeight: 600
                    }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                    minHeight: '48px',
                    '& fieldset': {
                      borderColor: '#e0e6ed'
                    },
                    '&:hover fieldset': {
                      borderColor: '#5e72e4'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5e72e4'
                    }
                  }
                }}
              />
            </Grid>

            {/* Celular y Correo */}
            <Grid item xs={12} md={6}>
              <MDInput
                label="Número de Celular"
                fullWidth
                variant="outlined"
                value={formData.celular}
                onChange={(e) => handleFormChange("celular", e.target.value)}
                error={!!formErrors.celular}
                helperText={formErrors.celular}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#344767',
                    transform: 'translate(14px, 16px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.85)',
                      fontSize: '0.875rem',
                      fontWeight: 600
                    }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                    minHeight: '48px',
                    '& fieldset': {
                      borderColor: '#e0e6ed'
                    },
                    '&:hover fieldset': {
                      borderColor: '#5e72e4'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5e72e4'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                label="Correo Electrónico"
                type="email"
                fullWidth
                variant="outlined"
                value={formData.correo}
                onChange={(e) => handleFormChange("correo", e.target.value)}
                error={!!formErrors.correo}
                helperText={formErrors.correo}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#344767',
                    transform: 'translate(14px, 16px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.85)',
                      fontSize: '0.875rem',
                      fontWeight: 600
                    }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                    minHeight: '48px',
                    '& fieldset': {
                      borderColor: '#e0e6ed'
                    },
                    '&:hover fieldset': {
                      borderColor: '#5e72e4'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5e72e4'
                    }
                  }
                }}
              />
            </Grid>

            {/* País y Ciudad dependientes */}
            <Grid item xs={12} md={6}>
              <MDInput
                select
                label="País de Residencia"
                fullWidth
                variant="outlined"
                value={formData.pais}
                onChange={(e) => {
                  handleFormChange("pais", e.target.value);
                  setFormData((prev) => ({ ...prev, ciudad: "" }));
                }}
                error={!!formErrors.pais}
                helperText={formErrors.pais}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#344767',
                    transform: 'translate(14px, 16px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.85)',
                      fontSize: '0.875rem',
                      fontWeight: 600
                    }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                    minHeight: '48px',
                    '& .MuiSelect-select': {
                      padding: '12px 14px'
                    },
                    '& fieldset': {
                      borderColor: '#e0e6ed'
                    },
                    '&:hover fieldset': {
                      borderColor: '#5e72e4'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5e72e4'
                    }
                  }
                }}
              >
                <MenuItem value="">Seleccione un país</MenuItem>
                {Object.keys(countryCityMap)
                  .sort()
                  .map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
              </MDInput>
            </Grid>

            <Grid item xs={12} md={6}>
              <MDInput
                select
                label="Ciudad de Residencia"
                fullWidth
                variant="outlined"
                value={formData.ciudad}
                onChange={(e) => handleFormChange("ciudad", e.target.value)}
                error={!!formErrors.ciudad}
                helperText={formErrors.ciudad}
                disabled={!formData.pais}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: formData.pais ? '#344767' : '#adb5bd',
                    transform: 'translate(14px, 16px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.85)',
                      fontSize: '0.875rem',
                      fontWeight: 600
                    }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                    minHeight: '48px',
                    backgroundColor: !formData.pais ? '#f8f9fa' : 'transparent',
                    '& .MuiSelect-select': {
                      padding: '12px 14px'
                    },
                    '& fieldset': {
                      borderColor: !formData.pais ? '#dee2e6' : '#e0e6ed'
                    },
                    '&:hover fieldset': {
                      borderColor: !formData.pais ? '#dee2e6' : '#5e72e4'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5e72e4'
                    },
                    '&.Mui-disabled': {
                      '& fieldset': {
                        borderColor: '#dee2e6'
                      }
                    }
                  }
                }}
              >
                <MenuItem value="">Seleccione una ciudad</MenuItem>
                {formData.pais &&
                  countryCityMap[formData.pais]?.map((city) => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  ))}
              </MDInput>
            </Grid>

            {/* Botones */}
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <MDButton
                  variant="outlined"
                  color="secondary"
                  onClick={handleCloseModal}
                  sx={{
                    minWidth: '120px',
                    height: '42px',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  Cancelar
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="primary"
                  onClick={handleSaveCandidate}
                  disabled={
                    !formData.documento ||
                    !formData.nombre ||
                    !formData.celular ||
                    !formData.correo ||
                    !formData.pais ||
                    !formData.ciudad ||
                    Object.values(formErrors).some((e) => e !== "")
                  }
                  sx={{
                    minWidth: '160px',
                    height: '42px',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  Guardar Candidato
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDModal>

      <Footer />

      {/* Notificaciones */}
      <MDSnackbar
        color={notification.color}
        icon="notifications"
        title="Notificación"
        content={notification.message || ""}
        dateTime={new Date().toLocaleTimeString()}
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
        bgWhite
      />
    </DashboardLayout>
  );
};

export default GestionMinoristasPasos;